import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import {
  Avatar,
  AvatarImage,
  AvatarFallback,
} from "@/components/ui/avatar";
import { Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { useState, useEffect, useRef } from "react";

import Json from "../../assets/images/testimonial/Jason.jpg";
import Priya from "../../assets/images/testimonial/Priya.jpg";
import Sonia from "../../assets/images/testimonial/Sonia.jpg";
import Alex from "../../assets/images/testimonial/Alex.jpg";
import Sophia from "../../assets/images/testimonial/Sophia.jpg";
import Michael from "../../assets/images/testimonial/Michael.jpg";
import Rohit from "../../assets/images/testimonial/Rohit.jpg";
import Neha from "../../assets/images/testimonial/Neha.jpg";

const testimonials = [
  {
    id: 1,
    name: "Jason Martis",
    role: "Marketing Manager",
    feedback:
      "Our outbound campaigns are generating more replies than ever before.",
    image: Json,
    stars: 5,
  },
  {
    id: 2,
    name: "Priya",
    role: "Business Owner",
    feedback:
      "I finally have a steady flow of new business enquiries every month.",
    image: Priya,
    stars: 5,
  },
  {
    id: 3,
    name: "Sonia Sharma",
    role: "Sales Director",
    feedback:
      "The lead data is accurate and helped us shorten our sales cycle.",
    image: Sonia,
    stars: 5,
  },
  {
    id: 4,
    name: "Alex Lee",
    role: "Startup Founder",
    feedback:
      "As a small team, MaxiPitch helped us scale outreach without hiring more staff.",
    image: Alex,
    stars: 5,
  },
  {
    id: 5,
    name: "Sophia Brown",
    role: "Freelancer",
    feedback:
      "I spend less time searching for clients and more time doing actual work.",
    image: Sophia,
    stars: 5,
  },
  {
    id: 6,
    name: "Michael Green",
    role: "Digital Marketer",
    feedback:
      "The targeting options are excellent. We reached exactly the audience we wanted.",
    image: Michael,
    stars: 4,
  },
  {
    id: 8,
    name: "Rohit Verma",
    role: "IT Consultant",
    feedback:
      "Within a few weeks we had more meetings booked than the previous quarter.",
    image: Rohit,
    stars: 5,
  },
  {
    id: 9,
    name: "Neha Kulkarni",
    role: "Agency Owner",
    feedback:
      "Simple to use, powerful results. It's now part of our daily workflow.",
    image: Neha,
    stars: 5,
  },
];

export default function TestimonialGrid() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollContainerRef = useRef(null);
  const scrollIntervalRef = useRef(null);

  const scrollToCard = (index) => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const cardWidth = container.children[0]?.offsetWidth || 0;
      const gap = 24;
      const scrollPosition = index * (cardWidth + gap);

      container.scrollTo({
        left: scrollPosition,
        behavior: "smooth",
      });
    }
  };

  const handleNext = () => {
    const nextIndex = (currentIndex + 1) % testimonials.length;

    setCurrentIndex(nextIndex);
    scrollToCard(nextIndex);
  };

  const handlePrev = () => {
    const prevIndex =
      (currentIndex - 1 + testimonials.length) %
      testimonials.length;

    setCurrentIndex(prevIndex);
    scrollToCard(prevIndex);
  };

  // Auto-scroll every 3 seconds
  useEffect(() => {
    scrollIntervalRef.current = setInterval(() => {
      handleNext();
    }, 3000);

    return () => clearInterval(scrollIntervalRef.current);
  }, [currentIndex]);

  return (
    <section className="relative overflow-hidden px-3 py-12 text-center text-black sm:px-5 md:px-6 md:py-20">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="mb-8 px-1 py-2 md:mb-12 md:px-6"
      >
        <span className="inline-flex rounded-full bg-gray-200 px-4 py-1 text-sm font-medium md:text-base">
          Testimonials
        </span>

        <h2 className="mx-auto mt-5 max-w-4xl text-[27px] font-bold leading-[1.2] text-gray-900 md:mt-6 md:text-4xl">
          What Our Clients Say About Maxipitch- Lead Generation
        </h2>

        <p className="mx-auto mt-4 max-w-2xl text-[15px] leading-7 text-gray-600 md:text-base">
          Our AI-powered lead generation platform helps businesses capture,
          nurture, and convert leads effortlessly. See what our happy customers
          say about their journey with us.
        </p>
      </motion.div>

      {/* Testimonial slider */}
      <div className="relative flex w-full items-center justify-center">
        {/* Previous button */}
        <button
          type="button"
          onClick={handlePrev}
          aria-label="Previous testimonial"
          className="absolute left-0 top-1/2 z-20 flex -translate-y-1/2 items-center justify-center rounded-full bg-gray-100 p-3 shadow transition-all duration-200 hover:bg-gray-200 max-md:left-1 max-md:h-11 max-md:w-11 max-md:border max-md:border-gray-200 max-md:bg-white max-md:p-0 max-md:shadow-md"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>

        {/* Next button */}
        <button
          type="button"
          onClick={handleNext}
          aria-label="Next testimonial"
          className="absolute right-0 top-1/2 z-20 flex -translate-y-1/2 items-center justify-center rounded-full bg-gray-100 p-3 shadow transition-all duration-200 hover:bg-gray-200 max-md:right-1 max-md:h-11 max-md:w-11 max-md:border max-md:border-gray-200 max-md:bg-white max-md:p-0 max-md:shadow-md"
        >
          <ChevronRight className="h-5 w-5" />
        </button>

        {/* Scrollable cards */}
        <div
          ref={scrollContainerRef}
          className="scrollbar-hide flex w-full justify-start gap-6 overflow-x-auto overflow-y-hidden px-0 py-4 pb-8 max-md:snap-x max-md:snap-mandatory max-md:px-10 md:justify-start"
          style={{
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            WebkitOverflowScrolling: "touch",
          }}
        >
          {testimonials.map((t, index) => (
            <motion.div
              key={t.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.7,
                delay: index * 0.1,
              }}
              whileHover={{ scale: 1.05 }}
              className="mx-0 w-80 flex-shrink-0 snap-start max-md:w-[calc(100vw-104px)] max-md:min-w-[260px] max-md:max-w-[320px] max-md:snap-center md:mx-0"
            >
              <Card className="h-full rounded-2xl border border-gray-200 bg-gray-50 shadow-md transition-shadow duration-300 hover:shadow-xl max-md:min-h-[280px] max-md:bg-white">
                <CardContent className="flex h-full flex-col justify-between p-4 max-md:p-5">
                  {/* Stars */}
                  <div className="mb-4 flex text-yellow-500">
                    {"★".repeat(t.stars)}
                  </div>

                  {/* Feedback */}
                  <p className="mb-5 text-left text-sm leading-relaxed text-gray-700 max-md:text-[15px] max-md:leading-7">
                    "{t.feedback}"
                  </p>

                  {/* Customer information */}
                  <div className="mt-auto flex items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center space-x-3">
                      <Avatar className="h-11 w-11 shrink-0">
                        <AvatarImage src={t.image} alt={t.name} />

                        <AvatarFallback>
                          {t.name?.[0] || "U"}
                        </AvatarFallback>
                      </Avatar>

                      <div className="min-w-0 text-left">
                        <h4 className="truncate text-base font-semibold text-gray-900">
                          {t.name}
                        </h4>

                        <p className="truncate text-sm text-gray-500">
                          {t.role}
                        </p>
                      </div>
                    </div>

                    <Quote className="h-5 w-5 shrink-0 text-gray-400" />
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Desktop gradient fades */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-20 bg-gradient-to-r from-white to-transparent max-md:hidden" />

        <div className="pointer-events-none absolute inset-y-0 right-0 w-20 bg-gradient-to-l from-white to-transparent max-md:hidden" />
      </div>

      {/* Dots indicator */}
      <div className="mt-4 flex flex-wrap justify-center gap-2 md:mt-6">
        {testimonials.map((_, index) => (
          <button
            key={index}
            type="button"
            aria-label={`Go to testimonial ${index + 1}`}
            onClick={() => {
              setCurrentIndex(index);
              scrollToCard(index);
            }}
            className={`h-2 rounded-full transition-all duration-300 ${index === currentIndex
              ? "w-5 bg-blue-500"
              : "w-2 bg-gray-300 hover:bg-gray-400"
              }`}
          />
        ))}
      </div>
    </section>
  );
}